// finviseMetrics.js
// Pure, framework-free helpers ported 1:1 from index_16.html so the React
// components reproduce the mockup's numbers and layout exactly.

export const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
export const BLUE = "#2f52c7";
export const INDIGO = "#3d4fc9";
export const NAVY = "#0a1059";
export const CURRENCY = "\u00a3";

// shared default range for both the Dashboard and Properties pages \u2014
// letting a parent page lift range state and hand the same object to
// both keeps the selected period in sync between them
export function defaultRange() {
    const now = new Date();
    const from = new Date(+now - 29 * 864e5);
    from.setHours(0, 0, 0, 0);
    const to = new Date(now);
    to.setHours(23, 59, 59, 999);
    return { from, to, label: "Last 30 days" };
}

export const STATUS = [
    { key: "Submitted",   color: "#0ca30c", text: "#006300" },
    { key: "In Progress", color: "#eda100", text: "#8a5e00" },
    { key: "Cancelled",   color: "#d03b3b", text: "#d03b3b" },
];

// Treemap colours (blue family) — matches renderDonut()
export const TM_COLORS = {
    "Submitted":   { fill: "#2f52c7", ink: "#ffffff" },
    "In Progress": { fill: "#8ea9ea", ink: "#0a1059" },
    "Cancelled":   { fill: "#0a1059", ink: "#ffffff" },
};

/* ── formatting ─────────────────────────────────────────── */
export const fmtMoney = (v, dp = 0) =>
    CURRENCY + Number(v).toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp });

export const fmtCompact = (v) => {
    if (v == null) return "\u2014";
    if (v >= 1e6) return CURRENCY + (v / 1e6).toLocaleString("en-GB", { maximumFractionDigits: 1 }) + "M";
    if (v >= 1e3) return CURRENCY + Math.round(v / 1e3).toLocaleString("en-GB") + "k";
    return fmtMoney(v);
};

// "2h 05m" style duration, for KPI tiles that measure time-in-hours
export const fmtHoursMinutes = (hours) => {
    if (hours == null || !isFinite(hours)) return null;
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${String(m).padStart(2, "0")}m`;
};

export const parse = (s) => new Date(s);
export const fmtDate = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
export const fmtDateTime = (d) =>
    `${fmtDate(d)}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
export const fmtShort = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
export const toISO = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const dayEnd = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
export const mLbl = (d) => MONTHS[d.getMonth()] + " " + String(d.getFullYear()).slice(2);

/* ── range filtering ────────────────────────────────────── */
export const inRange = (r, from, to) => {
    const d = +parse(r.instructed);
    return d >= +from && d <= +to;
};
export const filterRange = (data, from, to) => data.filter((r) => inRange(r, from, to));

export function prevRange(from, to) {
    const len = +to - +from;
    return [new Date(+from - len - 1), new Date(+from - 1)];
}
export const inPrev = (r, [f, t]) => {
    const d = +parse(r.instructed);
    return d >= +f && d <= +t;
};

/* ── metrics ────────────────────────────────────────────── */
export const avg = (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : null);

export function metrics(rows) {
    const submitted = rows.filter((r) => r.status === "Submitted");
    const turn = avg(
        submitted.filter((r) => r.submitted).map((r) => (+parse(r.submitted) - +parse(r.instructed)) / 864e5)
    );
    const fees = submitted.reduce((s, r) => s + (r.feeInc || 0), 0);
    const pvq = avg(rows.map((r) => r.pvqResponseHours).filter((v) => v != null));
    const vals = rows.map((r) => r.valuation).filter((v) => v != null);
    return {
        apps: rows.length,
        ricsApps: rows.filter((r) => r.type === "RICS").length,
        turn, fees, pvq,
        avgVal: avg(vals),
        totVal: vals.reduce((s, x) => s + x, 0),
    };
}

export function makeDelta(cur, prev, { pct = true, goodWhenDown = false, fmt = (x) => x } = {}) {
    if (prev == null || cur == null || !isFinite(prev) || prev === 0) return null;
    const diff = cur - prev;
    if (Math.abs(diff) < 1e-9) return { cls: "flat", arrow: "\u2192", text: "no change" };
    const up = diff > 0;
    const good = goodWhenDown ? !up : up;
    const text = pct ? Math.abs((diff / prev) * 100).toFixed(0) + "%" : fmt(Math.abs(diff));
    return { cls: good ? "up" : "down", arrow: up ? "\u2191" : "\u2193", text };
}

/* Total Fees window — full calendar months only (see renderKPIs/feeWindow). */
export function feeWindow(from, to) {
    let ws = from.getDate() === 1
        ? new Date(from.getFullYear(), from.getMonth(), 1)
        : new Date(from.getFullYear(), from.getMonth() + 1, 1);
    const lastDay = new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();
    let we = to.getDate() === lastDay
        ? new Date(to.getFullYear(), to.getMonth() + 1, 0, 23, 59, 59, 999)
        : new Date(to.getFullYear(), to.getMonth(), 0, 23, 59, 59, 999);
    if (+ws > +we) {
        we = new Date(to.getFullYear(), to.getMonth(), 0, 23, 59, 59, 999);
        ws = new Date(we.getFullYear(), we.getMonth(), 1);
        return { from: ws, to: we, fallback: true };
    }
    return { from: ws, to: we, fallback: false };
}

export function feesForWindow(data, fw) {
    return data
        .filter((r) => r.status === "Submitted" && +parse(r.instructed) >= +fw.from && +parse(r.instructed) <= +fw.to)
        .reduce((s, r) => s + (r.feeInc || 0), 0);
}

/* ── reports-by-month buckets ───────────────────────────── */
export function monthBuckets(rows, from, to) {
    const buckets = [];
    let d = new Date(from.getFullYear(), from.getMonth(), 1);
    const endM = new Date(to.getFullYear(), to.getMonth(), 1);
    while (d <= endM) {
        buckets.push({ y: d.getFullYear(), m: d.getMonth(), n: 0 });
        d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    rows.forEach((r) => {
        const dt = parse(r.instructed);
        const b = buckets.find((b) => b.y === dt.getFullYear() && b.m === dt.getMonth());
        if (b) b.n++;
    });
    const maxN = Math.max(0, ...buckets.map((b) => b.n));
    // fixed 10-per-tick scale with one full step of headroom above the
    // tallest bar (e.g. a max of 44 rounds up to 50, +10 = 60)
    const step = 10;
    const yMax = Math.ceil(maxN / step) * step + step;
    return { buckets, step, yMax };
}

/* ── status treemap (slice-and-dice) ────────────────────── */
export function treemapCells(rows, W, H, GAP = 2) {
    const counts = STATUS.map((s) => ({
        key: s.key,
        n: rows.filter((r) => r.status === s.key).length,
        fill: TM_COLORS[s.key].fill,
        ink: TM_COLORS[s.key].ink,
    }));
    const total = rows.length;
    const items = counts.filter((c) => c.n > 0).sort((a, b) => b.n - a.n);
    const cells = [];
    const place = (list, x, y, w, h) => {
        if (!list.length || w <= 0 || h <= 0) return;
        const sum = list.reduce((s, c) => s + c.n, 0);
        const c = list[0];
        const frac = c.n / sum;
        let cw, ch, nx, ny, nw, nh;
        if (list.length === 1) { cw = w; ch = h; }
        else if (w >= h) {
            cw = Math.max(0, w * frac - GAP / 2); ch = h;
            nx = x + cw + GAP; ny = y; nw = w - cw - GAP; nh = h;
        } else {
            cw = w; ch = Math.max(0, h * frac - GAP / 2);
            nx = x; ny = y + ch + GAP; nw = w; nh = h - ch - GAP;
        }
        cells.push({ ...c, x, y, w: cw, h: ch, total });
        if (list.length > 1) place(list.slice(1), nx, ny, nw, nh);
    };
    place(items, 0, 0, W, H);
    return { cells, counts, total };
}

/* ── rejection reasons aggregation ──────────────────────── */
export function rejectionCounts(rows, REASONS) {
    const cancelled = rows.filter((r) => r.status === "Cancelled");
    const predefined = REASONS.slice(0, -1);
    const otherLabel = REASONS[REASONS.length - 1];
    const counts = REASONS.map((reason) => ({
        reason,
        n: cancelled.filter((r) =>
            reason === otherLabel
                ? r.rejectionReason && !predefined.includes(r.rejectionReason)
                : r.rejectionReason === reason
        ).length,
    }));
    const max = Math.max(1, ...counts.map((c) => c.n));
    const rate = rows.length ? ((cancelled.length / rows.length) * 100).toFixed(1) + "%" : "\u2014";
    const top = counts.slice().sort((a, b) => b.n - a.n)[0];
    return { counts, max, cancelled: cancelled.length, rate, top };
}

/* ── dashboard: case status, instructions by month, year to date ─ */
export const PERI = "#9aa7ea";
export const NEUTRAL = "#c5c9d8";

// data-s ids are shared by the status bar segments and the legend so that
// hovering either one highlights both
export const CASE_STATUS = [
    { id: "done", key: "Submitted",   label: "Report completed", color: NAVY },
    { id: "prog", key: "In Progress", label: "In progress",      color: PERI },
    { id: "canc", key: "Cancelled",   label: "Cancelled",        color: NEUTRAL },
];

export function caseStatusSummary(rows) {
    const total = rows.length;
    const items = CASE_STATUS.map((s) => {
        const n = rows.filter((r) => r.status === s.key).length;
        return { ...s, n, pct: total ? Math.round((n / total) * 100) : 0 };
    });
    return { total, completed: items[0].n, inProgress: items[1].n, cancelled: items[2].n, items };
}

// "Instructions by month" always covers the current calendar year to date,
// independent of the page's date-range picker
export function ytdRange(now = new Date()) {
    return { from: new Date(now.getFullYear(), 0, 1), to: dayEnd(now) };
}

// one bucket per month from `from` to `to`, split by status
export function monthStatusBuckets(rows, from, to) {
    const buckets = [];
    let d = new Date(from.getFullYear(), from.getMonth(), 1);
    const endM = new Date(to.getFullYear(), to.getMonth(), 1);
    while (d <= endM) {
        buckets.push({ y: d.getFullYear(), m: d.getMonth(), done: 0, prog: 0, canc: 0, total: 0 });
        d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    rows.forEach((r) => {
        const dt = parse(r.instructed);
        const b = buckets.find((x) => x.y === dt.getFullYear() && x.m === dt.getMonth());
        if (!b) return;
        b.total++;
        if (r.status === "Submitted") b.done++;
        else if (r.status === "Cancelled") b.canc++;
        else b.prog++;
    });
    const maxN = Math.max(0, ...buckets.map((b) => b.total));
    // one tick step of headroom above the tallest bar so its label fits
    const yMax = Math.max(10, Math.ceil((maxN + 1) / 10) * 10);
    const step = yMax <= 50 ? 10 : yMax <= 100 ? 20 : 50;
    return { buckets, yMax: Math.ceil(yMax / step) * step, step };
}

// "4h 05m" → hours for KPI values; under an hour it reads in minutes
export function fmtDuration(hours) {
    if (hours == null || !isFinite(hours)) return null;
    if (hours < 1) return { value: String(Math.max(1, Math.round(hours * 60))), unit: "min" };
    return { value: fmtHoursMinutes(hours), unit: "avg" };
}
